import { Container, FloatingLabel, Form } from 'react-bootstrap';
import { FC, useEffect, useMemo, useState } from 'react';
import { generalBlue } from 'src/app-config';
import { generalHelpers } from '@monorepo/helpers';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { localStorageHelper, userServices } from 'src/services';
import { startAuthentication } from '@simplewebauthn/browser';
import { useDispatch } from 'react-redux';
import { useGetIsDarkMode, useGetIsMobile, useGetRecaptchaToken } from 'src/custom-hooks';

type propsType = {
    loginUsingToken: () => Promise<void>;
    openAlertModalHandler: (title: string, message: string, animation: number, execution?: Function) => void;
    returnUrl: string | null;
    usePasswordless: () => void;
};

export const PasswordAuthentication: FC<propsType> = ({
    loginUsingToken, openAlertModalHandler, returnUrl, usePasswordless
}) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const dispatch = useDispatch();
    const getRecaptchaToken = useGetRecaptchaToken();
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();

    const isValidEmail: boolean = useMemo(() =>
        generalHelpers.IsValidEmail(email)
    , [email]);

    const isValid: boolean = useMemo(() =>
        isValidEmail && password.length >= 3
    , [isValidEmail, password]);

    const loginHandler = async (): Promise<void> => {
        if (!isValid) {
            openAlertModalHandler('Problemas', 'Faltan datos', 2);
            return;
        }
        const recaptchaToken = await getRecaptchaToken();
        if (!recaptchaToken) {
            openAlertModalHandler('Problemas (1)', 'Refrescar la página', 2);
            return;
        }
        dispatch(showLoadingModalReducer());
        const response = await userServices.Login(email, password, recaptchaToken);
        dispatch(hideLoadingModalReducer());
        if (!response.success) {
            localStorageHelper.SetFailingEmail(email);
            openAlertModalHandler(response.message.title, response.message.description, 2);
            return;
        }
        loginUsingToken();
    }

    const recoverAccountHandler = (): void => {
        if (!isValidEmail) {
            openAlertModalHandler('Escribir el email primero', '', 2);
            return;
        }
        dispatch(setValuesAndOpenAlertModalReducer({
            mode: 'confirm',
            title: '¿Recuperar cuenta?',
            message: `Esto enviará un correo a ${email} para cambiar la contraseña`,
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await userServices.SendLinkToRecoverAccount(email);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    openAlertModalHandler(response.message.title, response.message.description, 2);
                    return;
                }
                openAlertModalHandler(`Se envió un correo a ${email}`, '', 1);
            }
        }));
    }

    const sendEmailOTP = (): void => {
        if (!isValidEmail) {
            openAlertModalHandler('Escribir el email primero', '', 2);
            return;
        }
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Confirmar',
            message: `Se va a enviar una contraseña de uso único el correo ${email}`,
            mode: 'confirm',
            execution: async () => {
                const recaptchaToken = await getRecaptchaToken();
                if (!recaptchaToken) {
                    openAlertModalHandler('Problemas (5)', 'Refrescar la página', 2);
                    return;
                }
                dispatch(showLoadingModalReducer());
                const response = await userServices.SendEmailOTP(email, recaptchaToken);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    openAlertModalHandler(response.message.title, response.message.description, 2);
                    return;
                }
                openAlertModalHandler(`Ahora ingresar en el campo de contraseña el código recibido en ${email}`, '', 1);
            }
        }));
    }

    const startLoginWithPasskey = async (useBrowserAutofill: boolean): Promise<void> => {
        if (!useBrowserAutofill) {
            dispatch(showLoadingModalReducer());
        }
        let response = await userServices.PasskeyStartLoginWith();
        dispatch(hideLoadingModalReducer());
        if (!response.success || !response.data?.passkeyChallengeObject) {
            if (!useBrowserAutofill) {
                dispatch(setAlertResponse({ response }));
            }
            return;
        }
        let assertionResp;
        try {
            assertionResp = await startAuthentication({
                optionsJSON: {
                    ...response.data.passkeyChallengeObject,
                    userVerification: useBrowserAutofill ? 'preferred' : 'required'
                },
                useBrowserAutofill  // conditional or undefined
            });
        } catch (error) {
            console.error(error);
            return;
        }
        dispatch(showLoadingModalReducer());
        const response1 = await userServices.PasskeyAuthenticate(response.data.passkeyChallengeObject.challenge, assertionResp);
        dispatch(hideLoadingModalReducer());
        if (!response1.success) {
            dispatch(setAlertResponse({ response: response1 }));
            return;
        }
        loginUsingToken();
    }

    useEffect(() => {
        startLoginWithPasskey(true);
    }, []);

    useEffect(() => {
        const failingEmail = localStorageHelper.GetFailingEmail();
        if (!failingEmail) {
            document.getElementById('emailInput')?.focus();
            return;
        }
        setEmail(failingEmail);
        document.getElementById('passwordInput')?.focus();
    }, []);

    return (
        <Container className={`${isDarkMode ? 'bg-dark' : 'bg-lightgrey'} border rounded`}
            style={{
                marginBottom: '50px',
                marginTop: isMobile ? '0' : '60px',
                maxWidth: '600px',
                padding: isMobile ? '20px 0 0' : '50px 0 0'
            }}
        >
            <h2 className={`${isDarkMode ? 'text-white' : ''} text-center mx-auto`}
                style={{
                    fontSize: isMobile ? '1.7rem' : '2rem',
                    maxWidth: '90%',
                    // textShadow: '0 0 1px gray'
                }}
            >
                INGRESAR {returnUrl && returnUrl !== '/' && `a ${returnUrl}`}
            </h2>
            <Container style={{ maxWidth: '500px', padding: isMobile ? '15px 10px 0' : '35px 0 0' }}>
                <FloatingLabel className={'text-dark mb-3'}
                    label={'Correo electrónico'}
                >
                    <Form.Control type={'email'}
                        id={'emailInput'}
                        className={'form-control'}
                        autoComplete={'username webauthn'}
                        placeholder={email}
                        value={email}
                        onChange={e => setEmail((e.target as HTMLInputElement).value)}
                    />
                </FloatingLabel>
                <FloatingLabel className={'text-dark mb-3'}
                    label={'Contraseña'}
                >
                    <Form.Control type={showPassword ? 'text' : 'password'}
                        id={'passwordInput'}
                        className={'form-control'}
                        autoComplete={'current-password '}
                        placeholder={password}
                        value={password}
                        onChange={e => setPassword((e.target as HTMLInputElement).value)}
                        onKeyDown={e => e.key === 'Enter' ? loginHandler() : null }
                    />
                </FloatingLabel>
                <button className={'btn btn-general-blue d-block w-100 mt-3'}
                    style={{ fontWeight: 'bolder', height: '50px' }}
                    onClick={loginHandler}
                    disabled={!isValid}
                >
                    ENTRAR
                </button>
                <button className={'btn btn-general-blue d-block w-100 mt-3'}
                    style={{ fontWeight: 'bolder', height: '50px' }}
                    onClick={() => startLoginWithPasskey(false)}
                >
                    USAR UNA LLAVE DE ACCESO
                </button>
                <div className={'row mt-4 mb-2'}>
                    <p className={'col-6 mb-1'} style={{ color: generalBlue, fontSize: '1rem' }}>
                        <span className={`pointer ${isDarkMode ? 'text-white' : ''}`}
                            onClick={() => setShowPassword(x => !x)}
                        >
                            {showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        </span>
                    </p>
                    <p className={'col-6 text-end m-0 mb-2'} style={{ color: generalBlue, fontSize: '1rem' }}>
                        <span className={`pointer ${isDarkMode ? 'text-white' : ''}`}
                            onClick={usePasswordless}
                        >
                            Ingresar por SMS
                        </span>
                    </p>
                    <p className={'col-6'} style={{ color: generalBlue, fontSize: '1rem' }}>
                        <span className={`pointer ${isDarkMode ? 'text-white' : ''}`}
                            onClick={recoverAccountHandler}
                        >
                            Olvidé mi contraseña
                        </span>
                    </p>
                    <p className={'col-6 text-end'} style={{ color: generalBlue, fontSize: '1rem' }}>
                        <span className={`pointer ${isDarkMode ? 'text-white' : ''}`}
                            onClick={sendEmailOTP}
                        >
                            Ingresar por Email
                        </span>
                    </p>
                </div>
            </Container>
        </Container>
    );
}
